import React from 'react';
import { castArray, compact } from 'lodash';
import PropTypes from 'prop-types';

import { cn } from 'shadcn/lib/utils';

import { ViewEntityAddInfoSummaryCardContainer } from './ViewEntityAddInfoSummaryCardContainer';

/**
 * Layout component for entity detail views that composites the header, primary card,
 * optional supplemental cards, and related information sections into a responsive grid.
 *
 * @param {Object} props Component props.
 * @param {React.ReactNode} props.header React node rendered at the top of the page; required.
 * @param {React.ReactNode} props.primaryInformationCard Main card displayed in the primary column; required.
 * @param {React.ReactNode|React.ReactNode[]} [props.additionalInformationCards] Optional card(s) rendered below the primary card.
 * @param {React.ReactNode|React.ReactNode[]} [props.relatedInformationSummaryCards] Optional summary card(s) rendered alongside the primary column.
 * @param {React.ReactNode} [props.relatedInformationTabView] Optional related information tabs rendered after the main content.
 * @returns {JSX.Element} Entity view layout.
 */
export function ViewEntityPage({
    additionalInformationCards,
    header,
    primaryInformationCard,
    relatedInformationSummaryCards,
    relatedInformationTabView,
}) {
    const additionalCards = compact(castArray(additionalInformationCards)) ?? [];
    const summaryCards = compact(castArray(relatedInformationSummaryCards)) ?? [];

    if (!header) {
        throw new Error('ViewEntityPage requires a `header` prop.');
    }

    if (!primaryInformationCard) {
        throw new Error('ViewEntityPage requires a `primaryInformationCard` prop.');
    }

    return (
        <div className="flex flex-col gap-6 lg:gap-8">
            <section data-slot="entity-header" className="flex flex-col gap-4">
                {header}
            </section>

            <div
                data-slot="entity-content"
                className={cn(
                    'flex flex-col gap-6 lg:gap-8',
                    summaryCards.length ? 'lg:grid lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]' : undefined,
                )}
            >
                <div data-slot="entity-primary-column" className="flex flex-col gap-6 lg:gap-8">
                    <section data-slot="entity-primary-card">{primaryInformationCard}</section>

                    {additionalCards.map((card, index) => (
                        <section key={card?.key ?? `additional-card-${index}`} data-slot="entity-additional-card">
                            {card}
                        </section>
                    ))}
                </div>

                {summaryCards.length > 0 ? <ViewEntityAddInfoSummaryCardContainer cards={summaryCards} /> : null}
            </div>

            {relatedInformationTabView ? (
                <section data-slot="entity-related-detail" className="flex flex-col gap-6 lg:gap-8">
                    {relatedInformationTabView}
                </section>
            ) : null}
        </div>
    );
}

ViewEntityPage.propTypes = {
    additionalInformationCards: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
    header: PropTypes.node.isRequired,
    primaryInformationCard: PropTypes.node.isRequired,
    relatedInformationSummaryCards: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
    relatedInformationTabView: PropTypes.node,
};

ViewEntityPage.defaultProps = {
    additionalInformationCards: undefined,
    relatedInformationSummaryCards: undefined,
    relatedInformationTabView: undefined,
};

ViewEntityPage.displayName = 'ViewEntityPage';
